import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { FontPreview } from '../components/FontPreview';
import { ScriptureInsight, downloadScripturePdf } from '../components/ScriptureInsight';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Type, 
  Image as ImageIcon,
  Loader2,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  Download,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ScrollAnimateIn } from '../hooks/useScrollAnimation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [filter, setFilter] = useState('all'); // all, font, poster
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [selectedFormats, setSelectedFormats] = useState({}); // Track selected format per item
  
  // Check for success redirect
  const sessionId = searchParams.get('session_id');
  const purchasedItemId = searchParams.get('item_id');
  const purchasedFormat = searchParams.get('format');
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchasedItem, setPurchasedItem] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Get token directly from localStorage as fallback (for post-Stripe redirect)
  const storedToken = localStorage.getItem('token');
  const effectiveToken = token || storedToken;

  useEffect(() => {
    fetchShopItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    // Refetch items when category filter changes
    fetchShopItems();
  }, [categoryFilter]);

  useEffect(() => {
    if (sessionId && purchasedItemId) {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }
      
      // Check if user has a token (from context or localStorage)
      if (effectiveToken) {
        verifyPurchase();
      } else {
        // If no token at all, redirect to login
        toast.error('Please log in to access your purchase');
        navigate(`/login?redirect=/shop/success?session_id=${sessionId}&item_id=${purchasedItemId}${purchasedFormat ? `&format=${purchasedFormat}` : ''}`);
      }
    }
  }, [sessionId, purchasedItemId, effectiveToken, authLoading]);

  const fetchShopItems = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'All') {
        params.append('category', categoryFilter);
      }
      const url = `${API_URL}/api/shop/items${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url);
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch shop items:', error);
      toast.error('Failed to load shop items');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/shop/categories`);
      setCategories(response.data.categories || ['All']);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const verifyPurchase = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/shop/verify/${sessionId}`,
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      if (response.data.success) {
        setPurchaseSuccess(true);
        setPurchasedItem(response.data.item);
        setTransactionId(response.data.transaction_id);
        toast.success('Payment verified! Your download is ready.');
      }
    } catch (error) {
      console.error('Failed to verify purchase:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error('Failed to verify purchase. Please contact support.');
    }
  };

  const handleDownload = async (format) => {
    if (!transactionId) {
      toast.error('Transaction not found');
      return;
    }
    
    setDownloading(format);
    try {
      const response = await axios.get(
        `${API_URL}/api/shop/download/${transactionId}?format=${format}`,
        { 
          headers: { Authorization: `Bearer ${effectiveToken}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${purchasedItem?.title || 'download'}_poster.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${format.toUpperCase()} successfully!`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    } finally {
      setDownloading(null);
    }
  };

  const handlePurchase = async (item, format = 'jpg') => {
    if (!effectiveToken) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    // For posters, require format selection
    if (item.item_type === 'poster' && !selectedFormats[item.id]) {
      toast.error('Please select a download format first');
      return;
    }

    const selectedFormat = item.item_type === 'poster' ? (selectedFormats[item.id] || 'jpg') : null;
    
    setPurchasing(item.id);
    try {
      const response = await axios.post(
        `${API_URL}/api/shop/checkout`,
        {
          item_id: item.id,
          origin_url: window.location.origin,
          format: selectedFormat
        },
        { headers: { Authorization: `Bearer ${effectiveToken}` } }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Failed to start checkout');
    } finally {
      setPurchasing(null);
    }
  };

  const selectFormat = (itemId, format) => {
    setSelectedFormats(prev => ({ ...prev, [itemId]: format }));
  };

  const filteredItems = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(item => item.item_type === filter);
  }, [items, filter]);

  const fonts = items.filter(i => i.item_type === 'font');
  const posters = items.filter(i => i.item_type === 'poster');

  if (purchaseSuccess && purchasedItem) {
    return (
      <div className="min-h-screen bg-[#0D0507]">
        <Header />
        <main className="container mx-auto px-4 py-16 pt-24">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#2F7248]/20 rounded-full flex items-center justify-center border border-[#2F7248]/40">
              <CheckCircle className="w-10 h-10 text-[#4ADE80]" />
            </div>
            <h1 className="text-3xl font-cinzel font-bold mb-4 text-[#F4EFE6]">Purchase Successful!</h1>
            <p className="text-[#C2B49A] mb-8 font-serif">
              Thank you for your purchase. Your {purchasedItem.item_type === 'font' ? 'font' : 'digital poster'} is ready for download.
            </p>
            
            <div className="p-6 card-surface rounded-lg mb-8">
              <h3 className="font-cinzel font-semibold mb-2 text-[#F4EFE6]">{purchasedItem.title}</h3>
              <p className="text-sm text-[#C2B49A] mb-4 font-serif">{purchasedItem.description}</p>
              <FontPreview 
                font={{
                  id: purchasedItem.font_id,
                  preview_text: purchasedItem.preview_text,
                  css_properties: purchasedItem.css_properties,
                  style_properties: purchasedItem.style_properties
                }}
                borderStyle={purchasedItem.border_style}
                className="mb-4"
              />
              
              {/* Single Download Button for Posters - format already selected before checkout */}
              {purchasedItem.item_type === 'poster' && (
                <div className="mt-6">
                  <Button
                    onClick={() => handleDownload(purchasedFormat || 'jpg')}
                    disabled={downloading}
                    className="w-full btn-primary rounded-lg"
                    data-testid="download-poster-btn"
                  >
                    {downloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download {(purchasedFormat || 'jpg').toUpperCase()} (5000×5000)
                  </Button>
                </div>
              )}
              
              {/* Download Option for Fonts */}
              {purchasedItem.item_type === 'font' && (
                <div className="mt-6">
                  <Button
                    onClick={() => handleDownload('css')}
                    disabled={downloading === 'css'}
                    className="w-full btn-primary rounded-lg"
                    data-testid="download-css-btn"
                  >
                    {downloading === 'css' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Font CSS
                  </Button>
                </div>
              )}
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="btn-secondary rounded-lg"
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setPurchaseSuccess(false);
                  navigate('/shop');
                }}
                className="btn-secondary rounded-lg"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen stained-glass-shop">
      <Header />
      
      {/* Light ray effect at top */}
      <div className="stained-glass-light-ray" />
      
      <main className="container mx-auto px-4 py-12 pt-24 relative">
        {/* Rose window decoration behind hero */}
        <div className="rose-window">
          {/* Hero Section */}
          <ScrollAnimateIn animation="fade-up" className="text-center mb-12 relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 wine-glass rounded-full mb-4 stained-glass-shimmer">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-cinzel font-medium tracking-wider text-[#D4AF37]">AI-Generated Collection</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-cinzel font-bold mb-4 text-[#F4EFE6]">
              Shop Digital Posters
            </h1>
            <p className="text-lg text-[#C2B49A] max-w-2xl mx-auto font-serif">
              Discover unique AI-generated digital posters. 
              Each design is crafted with precision and creativity.
            </p>
          </ScrollAnimateIn>
        </div>

        {/* Stained glass divider */}
        <div className="stained-glass-divider" />

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm font-cinzel font-medium text-[#C2B49A]">Filter by Category</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setCategoryFilter(cat);
                    setLoading(true);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-cinzel font-medium transition-all ${
                    categoryFilter === cat
                      ? 'bg-[#722F37] text-[#F4EFE6] border border-[#D4AF37]/30'
                      : 'bg-[#160A0D] text-[#C2B49A] border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]'
                  }`}
                  data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-16 h-16 mb-6">
              <svg
                className="animate-gothic-spin"
                viewBox="0 0 50 50"
                style={{ width: 64, height: 64 }}
              >
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  fill="none"
                  stroke="url(#shopGradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="80 40"
                />
                <defs>
                  <linearGradient id="shopGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#D4AF37" />
                    <stop offset="50%" stopColor="#C5A059" />
                    <stop offset="100%" stopColor="#722F37" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl text-[#D4AF37] animate-candle">✦</span>
              </div>
            </div>
            <p className="font-cinzel text-[#C2B49A]">Loading sacred collection...</p>
            <div className="loading-bar w-48 mt-4 rounded-full" />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 card-surface rounded-full flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-[#C2B49A]" />
            </div>
            <h3 className="text-xl font-cinzel font-semibold mb-2 text-[#C2B49A]">No items in shop yet</h3>
            <p className="text-[#C2B49A]/60 mb-6 font-serif">Check back soon for new digital posters!</p>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="btn-secondary rounded-lg"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        )}

        {/* Items Grid with stagger animation - Stained Glass Layout */}
        {!loading && filteredItems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => {
              // Cycle through stained glass colors
              const glassColors = ['wine', 'blue', 'green', 'gold', 'purple'];
              const glassColor = glassColors[index % glassColors.length];
              
              return (
                <ScrollAnimateIn 
                  key={item.id}
                  animation="fade-up"
                  delay={index * 80}
                >
                  <div 
                    className={`stained-glass-panel ${glassColor} rounded-lg overflow-hidden group h-full`}
                    data-testid={`shop-item-${item.id}`}
                  >
                  {/* Preview */}
                  <div className="p-4 bg-[#160A0D]/50">
                    <FontPreview 
                      font={{
                        id: item.font_id,
                        preview_text: item.preview_text,
                        css_properties: item.css_properties,
                        style_properties: item.style_properties
                      }}
                      borderStyle={item.border_style}
                      borderColor={item.border_color_hex || item.border_color || '#D4AF37'}
                    />
                  </div>
                  
                  {/* Details */}
                  <div className="p-5 bg-[#220F13]/80 backdrop-blur-sm">
                    <div className="flex items-center gap-2 mb-2">
                      {item.item_type === 'font' ? (
                        <Type className="w-4 h-4 text-[#D4AF37]" />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                      )}
                      <span className="text-xs font-cinzel font-medium text-[#D4AF37] uppercase tracking-wider">
                        {item.item_type === 'font' ? 'Font CSS' : 'Digital Poster'}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-cinzel font-semibold mb-1 text-[#F4EFE6] group-hover:text-[#D4AF37] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#C2B49A] mb-3 line-clamp-2 font-serif">
                      {item.description}
                    </p>

                    {/* Scripture Insight for shop items */}
                    {(item.scripture_meaning || item.scripture_significance) && (
                      <div className="mb-3">
                        <ScriptureInsight
                          meaning={item.scripture_meaning}
                          significance={item.scripture_significance}
                          previewText={item.preview_text}
                          onDownloadPdf={() => downloadScripturePdf(`/api/scripture-pdf/shop/${item.id}`)}
                        />
                      </div>
                    )}
                    
                    {/* Download formats selection for posters */}
                    {item.item_type === 'poster' && (
                      <div className="mb-3 p-3 bg-[#0D0507]/60 rounded-lg border border-[#D4AF37]/20">
                        <p className="text-xs font-cinzel font-medium text-[#C2B49A] mb-2">Select Download Format:</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => selectFormat(item.id, 'jpg')}
                            className={`flex-1 text-xs py-2 px-3 rounded-lg border transition-all flex items-center justify-center gap-1 font-cinzel ${
                              selectedFormats[item.id] === 'jpg' 
                                ? 'bg-[#722F37] text-[#F4EFE6] border-[#D4AF37]/40' 
                                : 'bg-[#0D0507] text-[#C2B49A] border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                            }`}
                            data-testid={`select-jpg-${item.id}`}
                          >
                            <Download className="w-3 h-3" /> JPG 5000×5000
                          </button>
                          <button
                            onClick={() => selectFormat(item.id, 'png')}
                            className={`flex-1 text-xs py-2 px-3 rounded-lg border transition-all flex items-center justify-center gap-1 font-cinzel ${
                              selectedFormats[item.id] === 'png' 
                                ? 'bg-[#722F37] text-[#F4EFE6] border-[#D4AF37]/40' 
                                : 'bg-[#0D0507] text-[#C2B49A] border-[#D4AF37]/20 hover:border-[#D4AF37]/40'
                            }`}
                            data-testid={`select-png-${item.id}`}
                          >
                            <Download className="w-3 h-3" /> PNG Transparent
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-cinzel font-bold text-[#D4AF37]">
                        ${item.price.toFixed(2)}
                      </span>
                      <Button
                        onClick={() => handlePurchase(item)}
                        disabled={purchasing === item.id || (item.item_type === 'poster' && !selectedFormats[item.id])}
                        className={`rounded-lg ${
                          item.item_type === 'poster' && !selectedFormats[item.id]
                            ? 'bg-[#C2B49A]/30 cursor-not-allowed text-[#C2B49A]'
                            : 'btn-primary'
                        }`}
                        data-testid={`buy-${item.id}`}
                      >
                        {purchasing === item.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <ShoppingCart className="w-4 h-4 mr-2" />
                        )}
                        {item.item_type === 'poster' && !selectedFormats[item.id] ? 'Select Format' : 'Buy Now'}
                      </Button>
                    </div>
                  </div>
                </div>
                </ScrollAnimateIn>
              );
            })}
          </div>
        )}

        {/* Stained glass divider before footer */}
        <div className="stained-glass-divider mt-12" />

        {/* Back Button */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="btn-secondary rounded-lg"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
}
